import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, ApiError } from "../api/client";
import type { User } from "../types";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<User>;
  register: (data: {
    username: string;
    name: string;
    password: string;
    email?: string;
    curriculum_id?: string;
    /** Subjects ticked at sign-up — enrolled immediately. */
    subject_ids?: string[];
    /** curriculum_id -> chosen teacher, where a subject has more than one. */
    subject_instructors?: Record<string, string>;
    /** Whether answers may go to the AI marker. Answered on the form. */
    ai_consent?: boolean;
  }) => Promise<User>;
  logout: () => Promise<void>;
  /** Replace the cached account after the user edits their own details, so
      the nav and every page reading `user` show the new name immediately. */
  applyUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Wipe ALL cached query data on any auth change so one account can never see
  // another's cached pages (e.g. a shared browser switching learners).
  const resetCache = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  // Fetch current user on mount
  useEffect(() => {
    apiGet<User>("/api/auth/me")
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // If the session is invalidated server-side (logged in elsewhere, or it timed
  // out from inactivity), drop auth state so ProtectedRoute redirects to login.
  useEffect(() => {
    const onExpired = () => {
      resetCache();
      setUser(null);
    };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, [resetCache]);

  // Inactivity-based session: real user activity (throttled to once/60s) slides
  // the expiry forward via keepalive, so an active learner is never cut off,
  // while genuine idleness still times the session out.
  useEffect(() => {
    if (!user) return;
    let last = 0;
    const ping = () => {
      const now = Date.now();
      if (now - last < 60_000) return; // throttle
      last = now;
      apiPost<{ session_expires_at: string }>("/api/auth/keepalive")
        .then((r) =>
          setUser((u) => (u ? { ...u, session_expires_at: r.session_expires_at } : u))
        )
        .catch(() => {}); // a 401 here is handled by the auth:expired listener
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, ping, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, ping));
  }, [user?.id]);

  const login = useCallback(async (username: string, password: string) => {
    const u = await apiPost<User>("/api/auth/login", { username, password });
    resetCache();
    setUser(u);
    return u;
  }, [resetCache]);

  const register = useCallback(
    async (data: {
      username: string;
      name: string;
      password: string;
      email?: string;
      curriculum_id?: string;
      subject_ids?: string[];
      subject_instructors?: Record<string, string>;
      ai_consent?: boolean;
    }) => {
      const u = await apiPost<User>("/api/auth/register", data);
      resetCache();
      setUser(u);
      return u;
    },
    [resetCache]
  );

  const logout = useCallback(async () => {
    try {
      await apiPost<void>("/api/auth/logout");
    } catch (e) {
      if (e instanceof ApiError && e.status !== 401) throw e;
    }
    resetCache();
    setUser(null);
  }, [resetCache]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, applyUser: setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
